import { useEffect, useRef } from 'react';
import Icon, { CustomIconProps, IconName } from '../../Icon';
import useFilesDragNDrop from './useFilesDragNDrop';
import { classnames } from '@/libs/utils';
import { showErrorToast } from '@/libs/toast/toast';
import classes from './FilesInput.module.scss';

export default function FilesInput({
  onFilesUpload,
  uploadedFiles = [],
  label = 'Click to browse, or drag & drop a file here',
  icon = 'IconCloudUpload',
  multiple = true,
  accept,
  rootClassNames = '',
  containerClassNames = '',
  labelClassNames = '',
  iconClassNames = '',
  titleClassNames = '',
  title = '',
  iconProps = {},
  variant = 'default',
  isDisabled = false,
  buttonClassNames = '',
  disabledErrorMessage = 'File upload is not available at this time'
}: {
  onFilesUpload: (files: File[]) => void;
  uploadedFiles?: File[];
  label?: string;
  icon?: IconName;
  accept: string;
  multiple?: boolean;
  rootClassNames?: string;
  containerClassNames?: string;
  labelClassNames?: string;
  iconClassNames?: string;
  titleClassNames?: string;
  title?: string;
  iconProps?: Omit<CustomIconProps, 'name'>;
  variant?: 'default' | 'button';
  isDisabled?: boolean;
  buttonClassNames?: string;
  disabledErrorMessage?: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  const { isDragging, handleFilesToUpload, uploadFiles, handleDragEnter, handleDragOver, handleDragLeave } =
    useFilesDragNDrop({
      onFilesUpload,
      uploadedFiles,
      accept
    });

  useEffect(() => {
    if (window.openFilePickerOnDialogLoad) {
      window.openFilePickerOnDialogLoad = false;

      if (window.filesPickedForUpload?.length) {
        handleFilesToUpload(window.filesPickedForUpload);
        window.filesPickedForUpload = [];
      } else {
        fileInput.current?.click();
      }
    }
  }, []);

  if (variant === 'button') {
    return (
      <button
        type="button"
        className={`focus-visible:outline-hidden inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium ring-offset-white transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ${buttonClassNames}`}
        disabled={isDisabled}
        onClick={() => {
          fileInput.current?.click();
        }}
      >
        <Icon className={iconClassNames} name={icon} />
        <span>{title}</span>
        <input
          id="files-input-button"
          type="file"
          accept={accept}
          className="hidden"
          ref={fileInput}
          onChange={uploadFiles}
          multiple={multiple}
          data-testid="files-input-button"
        />
      </button>
    );
  }

  const handleClick = () => {
    if (isDisabled) {
      showErrorToast(disabledErrorMessage);
      return;
    }
    fileInput.current?.click();
  };

  const handleDrop = (e: React.DragEvent) => {
    if (isDisabled) {
      showErrorToast(disabledErrorMessage);
      return;
    }
    uploadFiles(e);
  };

  return (
    <div
      className={classnames(
        'size-full transition-all duration-300 ease-in-out',
        isDisabled ? 'cursor-not-allowed opacity-50' : '',
        rootClassNames
      )}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      data-testid="files-container"
    >
      <div
        className={classnames(
          'relative flex h-full flex-col items-center justify-center !rounded-xl border border-slate-300 p-4 transition-all duration-150 ease-in-out hover:transition-all hover:duration-150 hover:ease-in-out',
          isDisabled ? '' : 'hover:cursor-pointer',
          containerClassNames || classes['upload-container'],
          isDragging ? '!border-transparent' : ''
        )}
      >
        {isDragging && (
          <div className="absolute z-10 flex size-full items-center justify-center !rounded-xl bg-card-active-bg">
            <span className="text-xl font-medium">Drop it like it's hot</span>
          </div>
        )}
        <Icon
          name={icon}
          size={28}
          stroke={1.25}
          className={classnames('text-slate-500', classes['upload-icon'], iconClassNames)}
          {...iconProps}
        />
        <input
          type="file"
          accept={accept}
          className="invisible h-0"
          ref={fileInput}
          onChange={isDisabled ? undefined : uploadFiles}
          multiple={multiple}
          data-testid="files-input"
        />
        {!!title && <div className={classnames('mt-3.5 text-sm', titleClassNames)}>{title}</div>}
        <p
          className={classnames(
            'mt-2 max-w-40 text-center text-sm leading-tight text-slate-500 transition-all duration-150 ease-in-out',
            isDragging ? 'opacity-20' : '',
            labelClassNames
          )}
        >
          {label}
        </p>
      </div>
    </div>
  );
}
